import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faCheck, faTriangleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons';

// Ожидаемые заголовки → поле в базе
// Сравниваем без учёта регистра, пробелов и знаков препинания
const HEADER_MAP = [
  { field: 'partNumber', patterns: ['артикул'] },
  { field: 'brand',      patterns: ['бренд'] },
  { field: 'analog',     patterns: ['оригиналаналог', 'оригиналаналога'] },
  { field: 'name',       patterns: ['наименование'] },
  { field: 'stock',      patterns: ['остаток', 'остатокшт'] },
  { field: 'priceRub',   patterns: ['цена₽склад', 'ценарубсклад', 'ценасклад'] },
  { field: 'priceEur',   patterns: ['цена€заказ', 'ценаеврозаказ', 'ценазаказ'] },
  { field: 'weight',     patterns: ['вескг', 'вес'] },
  { field: 'supply',     patterns: ['поставка'] },
  { field: 'comment',    patterns: ['комментарий', 'примечание'] },
];

const norm = (s) => String(s || '')
  .toLowerCase()
  .replace(/[\s.,:;()«»"'/\\]/g, '')
  .replace(/\u00a0/g, '');

// Определяем, какому полю соответствует заголовок
const matchField = (header) => {
  const h = norm(header);
  for (const { field, patterns } of HEADER_MAP) {
    for (const p of patterns) {
      if (h === p || h.startsWith(p) || h.includes(p)) return field;
    }
  }
  return null;
};

// Найти строку заголовков на листе (первые 6 строк)
const findHeaderRow = (rows) => {
  for (let r = 0; r < Math.min(rows.length, 6); r++) {
    const row = rows[r] || [];
    const mapped = row.map(cell => matchField(cell));
    const found = mapped.filter(Boolean);
    if (found.length >= 3) { // хотя бы 3 колонки опознали
      return { headerIndex: r, mapping: mapped };
    }
  }
  return null;
};

const cellToString = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') {
    // Excel-даты приходят числом, но у нас их нет — просто вернём число
    return String(v);
  }
  return String(v).trim();
};

const PriceImporter = ({ onClose, onImport, existingCount = 0 }) => {
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState([]); // [{type, rows: [...]}]
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null); // {created, skipped, errors}

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setError('');
    setParsed([]);
    setResult(null);

    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });

      const allSheets = [];
      for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' });
        if (!rows.length) continue;

        const header = findHeaderRow(rows);
        if (!header) {
          allSheets.push({ type: sheetName, rows: [], skippedReason: 'Не найдена строка заголовков' });
          continue;
        }

        const { headerIndex, mapping } = header;
        const dataRows = [];
        for (let r = headerIndex + 1; r < rows.length; r++) {
          const row = rows[r] || [];
          const obj = {};
          mapping.forEach((field, colIdx) => {
            if (!field) return;
            obj[field] = cellToString(row[colIdx]);
          });
          // пропускаем пустые строки
          if (!obj.partNumber && !obj.name) continue;

          // «Тип» берём из имени листа (Культиваторы, Молотковые ножи…)
          obj.type = sheetName;
          dataRows.push(obj);
        }

        allSheets.push({ type: sheetName, rows: dataRows });
      }

      setParsed(allSheets);
      if (allSheets.every(s => s.rows.length === 0)) {
        setError('Не удалось распознать ни одной строки. Проверьте, что заголовки колонок — «Артикул», «Наименование» и т.д.');
      }
    } catch (err) {
      console.error(err);
      setError('Не удалось прочитать файл. Убедитесь, что это .xlsx/.xls');
    }
  };

  const totalRows = parsed.reduce((acc, s) => acc + s.rows.length, 0);

  const handleImport = async () => {
    if (totalRows === 0) return;
    setImporting(true);
    setResult(null);

    // собираем плоский массив
    const allItems = [];
    parsed.forEach(s => s.rows.forEach(r => allItems.push(r)));

    try {
      const res = await onImport(allItems);
      setResult(res);
    } catch (err) {
      setError(err.message || 'Ошибка импорта');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="task-modal admin-modal price-import-modal">
        <header className="task-modal__header">
          <h2 className="task-modal__title">
            <FontAwesomeIcon icon={faFileExcel} style={{ marginRight: 8, color: '#16a34a' }} />
            Импорт прайса из Excel
          </h2>
          <button className="task-modal__close" onClick={onClose}>×</button>
        </header>

        <div className="task-modal__body">
          <div className="price-import-upload">
            <label className="price-import-drop">
              <FontAwesomeIcon icon={faFileExcel} />
              <span>{fileName || 'Выбрать .xlsx файл'}</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFile}
                style={{ display: 'none' }}
              />
            </label>
            <div className="price-import-hint">
              Каждый лист файла — отдельная категория (тип). Колонки распознаются по заголовкам:
              Артикул, Бренд, Оригинал/Аналог, Наименование, Остаток, Цена ₽ Склад, Цена € Заказ, Вес, Поставка, Комментарий.
            </div>
          </div>

          {error && (
            <div className="price-import-error">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
            </div>
          )}

          {parsed.length > 0 && (
            <div className="price-import-preview">
              {parsed.map(sheet => (
                <div key={sheet.type} className="price-import-sheet">
                  <div className="price-import-sheet__head">
                    <span className="price-import-sheet__name">{sheet.type}</span>
                    <span className="price-import-sheet__count">{sheet.rows.length} строк</span>
                  </div>
                  {sheet.rows.length === 0 && sheet.skippedReason && (
                    <div className="price-import-sheet__skip">{sheet.skippedReason}</div>
                  )}
                  {sheet.rows.length > 0 && (
                    <div className="price-import-sheet__sample">
                      {sheet.rows.slice(0, 3).map((r, i) => (
                        <div key={i} className="price-import-sheet__row">
                          <b>{r.partNumber || '—'}</b>
                          <span>{r.name || '—'}</span>
                        </div>
                      ))}
                      {sheet.rows.length > 3 && (
                        <div className="price-import-sheet__more">…и ещё {sheet.rows.length - 3}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {result && (
            <div className="price-import-result">
              <div className="price-import-result__item">
                <FontAwesomeIcon icon={faCheck} style={{ color: '#059669' }} />
                Создано: <b>{result.created}</b>
              </div>
              {result.skipped > 0 && (
                <div className="price-import-result__item">
                  Пропущено (дубликаты): <b>{result.skipped}</b>
                </div>
              )}
              {result.errors > 0 && (
                <div className="price-import-result__item">
                  Ошибок: <b>{result.errors}</b>
                </div>
              )}
              {result.errorSamples?.length > 0 && (
                <div className="price-import-result__errors">
                  {result.errorSamples.slice(0, 5).map((e, i) => (
                    <div key={i}>— {e}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <footer className="task-modal__footer">
          <button className="task-modal__btn task-modal__btn--ghost" onClick={onClose}>
            {result ? 'Закрыть' : 'Отмена'}
          </button>
          {!result && (
            <button
              className="task-modal__btn task-modal__btn--primary"
              onClick={handleImport}
              disabled={importing || totalRows === 0}
            >
              {importing
                ? <><FontAwesomeIcon icon={faSpinner} spin /> Импортируем…</>
                : `Импортировать ${totalRows} строк`}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default PriceImporter;